import React, { useEffect, useRef } from 'react';

const CirclesBlanksPage = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const specifications = [
    {
      title: "Alloy Series",
      items: ["1100 Series", "1040 Series", "1050 Series", "1070 Series"],
      icon: "fas fa-atom"
    },
    {
      title: "Tempers Available",
      items: ["O (Annealed)", "H14 (Half Hard)", "H12 (Quarter Hard)", "H34 (3/4 Hard)", "Fully Hardened"],
      icon: "fas fa-hammer"
    },
    {
      title: "Size Range",
      items: ["Minimum: 2.5 inches", "Maximum: 48 inches", "Custom sizes available", "Precision cutting"],
      icon: "fas fa-ruler-combined"
    },
    {
      title: "Thickness Range",
      items: ["Minimum: 0.05mm", "Maximum: 4mm", "High precision tolerance", "Consistent quality"],
      icon: "fas fa-layer-group"
    }
  ];

  const applications = [
    {
      title: "Cookware Manufacturing",
      description: "High-quality circles for pressure cookers, pans, and kitchen utensils",
      icon: "fas fa-kitchen-set",
      uses: ["Pressure cooker bodies", "Frying pans", "Sauce pans", "Kitchen utensils"]
    },
    {
      title: "Lighting Reflectors",
      description: "Precision circles for efficient light reflection and distribution",
      icon: "fas fa-lightbulb",
      uses: ["LED reflectors", "Street light reflectors", "Industrial lighting", "Decorative fixtures"]
    },
    {
      title: "Cans & Containers",
      description: "Food-grade aluminium circles for packaging industry",
      icon: "fas fa-box",
      uses: ["Food cans", "Beverage containers", "Storage containers", "Industrial packaging"]
    },
    {
      title: "Bottles & Caps",
      description: "Specialized circles for bottle manufacturing and closures",
      icon: "fas fa-wine-bottle",
      uses: ["Bottle bodies", "Bottle caps", "Cosmetic containers", "Pharmaceutical packaging"]
    }
  ];

  const qualityFeatures = [
    {
      title: "Mill Finish Surface",
      description: "Superior surface quality with consistent finish throughout",
      icon: "fas fa-magic"
    },
    {
      title: "Precision Manufacturing",
      description: "Advanced machinery ensuring exact dimensions and tolerances",
      icon: "fas fa-cogs"
    },
    {
      title: "Quality Control",
      description: "Rigorous testing at every stage of production",
      icon: "fas fa-shield-halved"
    },
    {
      title: "Custom Solutions",
      description: "Tailored specifications to meet specific requirements",
      icon: "fas fa-tools"
    }
  ];

  return (
    <div className="circles-blanks-page">
      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .circles-blanks-page {
          font-family: 'Inter', 'Poppins', sans-serif;
          line-height: 1.6;
          color: #1E2329;
          overflow-x: hidden;
        }

        /* Hero Section */
        .hero {
          background: linear-gradient(135deg, #5A1FB3 0%, #1E2329 100%);
          padding: 120px 0 80px;
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="%23ffffff" opacity="0.05"><polygon points="0,0 1000,0 1000,100 0,80"/></svg>');
          background-size: cover;
        }

        .hero-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          text-align: center;
          position: relative;
          z-index: 2;
        }

        .hero h1 {
          font-size: 3.5rem;
          font-weight: 800;
          color: white;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1.3rem;
          color: rgba(255, 255, 255, 0.9);
          max-width: 800px;
          margin: 0 auto 2rem;
          font-weight: 400;
        }

        .hero-icon {
          width: 80px;
          height: 80px;
          background: #7B3FE4;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          box-shadow: 0 10px 30px rgba(123, 63, 228, 0.3);
        }

        .hero-icon i {
          font-size: 2rem;
          color: white;
        }

        /* Container */
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-header h2 {
          font-size: 2.8rem;
          font-weight: 700;
          background: linear-gradient(135deg, #7B3FE4 0%, #5A1FB3 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 1rem;
        }

        .section-header p {
          font-size: 1.2rem;
          color: #4A5260;
          max-width: 600px;
          margin: 0 auto;
        }

        /* Production Showcase */
        .production-showcase {
          padding: 100px 0;
          background: linear-gradient(135deg, #F6F7F9 0%, #EDE7FF 100%);
        }

        .showcase-image-container {
          position: relative;
          border-radius: 25px;
          overflow: hidden;
          box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
          margin-bottom: 3rem;
          background: white;
        }

        .showcase-image-container img {
          width: 100%;
          height: 500px;
          object-fit: cover;
          transition: all 0.3s ease;
        }

        .showcase-image-container:hover img {
          transform: scale(1.05);
        }

        .image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
          color: white;
          padding: 2rem;
          transform: translateY(100%);
          transition: transform 0.4s ease;
        }

        .showcase-image-container:hover .image-overlay {
          transform: translateY(0);
        }

        .image-overlay h3 {
          font-size: 1.8rem;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }

        .image-overlay p {
          font-size: 1.1rem;
          opacity: 0.9;
        }

        .image-placeholder-content {
          text-align: center;
          color: #7B3FE4;
        }

        .image-placeholder-content i {
          font-size: 4rem;
          margin-bottom: 1.5rem;
          display: block;
        }

        .image-placeholder-text {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .image-placeholder-desc {
          font-size: 1rem;
          opacity: 0.8;
          max-width: 400px;
        }

        /* Specifications Section */
        .specs-section {
          padding: 100px 0;
          background: white;
        }

        .specs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2.5rem;
        }

        .spec-card {
          background: white;
          padding: 3rem 2.5rem;
          border-radius: 20px;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
          text-align: center;
          transition: all 0.4s ease;
          border: 2px solid transparent;
          position: relative;
          overflow: hidden;
        }

        .spec-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 4px;
          background: linear-gradient(90deg, #7B3FE4, #14B87A);
        }

        .spec-card:hover {
          transform: translateY(-10px);
          border-color: rgba(123, 63, 228, 0.3);
          box-shadow: 0 25px 70px rgba(0, 0, 0, 0.15);
        }

        .spec-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #7B3FE4, #9A60FF);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          transition: all 0.3s ease;
        }

        .spec-card:hover .spec-icon {
          transform: scale(1.1) rotate(5deg);
        }

        .spec-icon i {
          font-size: 2rem;
          color: white;
        }

        .spec-card h3 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1E2329;
          margin-bottom: 1.5rem;
        }

        .spec-list {
          list-style: none;
          text-align: left;
        }

        .spec-list li {
          padding: 0.5rem 0;
          color: #4A5260;
          position: relative;
          padding-left: 1.5rem;
          font-weight: 500;
        }

        .spec-list li::before {
          content: '▶';
          position: absolute;
          left: 0;
          color: #7B3FE4;
          font-size: 0.8rem;
          top: 50%;
          transform: translateY(-50%);
        }

        /* Applications Section */
        .applications-section {
          padding: 100px 0;
          background: linear-gradient(135deg, #F6F7F9 0%, #EDE7FF 100%);
        }

        .applications-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 3rem;
        }

        .application-card {
          background: white;
          border-radius: 25px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
          transition: all 0.5s ease;
        }

        .application-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.12);
        }

        .app-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
          min-height: 400px;
        }

        .app-media {
          position: relative;
          overflow: hidden;
          background: #F7FAFC;
        }

        .app-media img {
          width: 100%;
          height: 100%;
          min-height: 400px;
          object-fit: cover;
          transition: all 0.3s ease;
        }

        .application-card:hover .app-media img {
          transform: scale(1.05);
        }

        .app-content {
          padding: 3rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .app-header {
          display: flex;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .app-icon {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, #7B3FE4, #9A60FF);
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1.5rem;
          transition: all 0.3s ease;
        }

        .application-card:hover .app-icon {
          transform: scale(1.1) rotate(5deg);
        }

        .app-icon i {
          font-size: 2rem;
          color: white;
        }

        .app-header h3 {
          font-size: 1.8rem;
          font-weight: 700;
          color: #1E2329;
        }

        .app-description {
          color: #4A5260;
          margin-bottom: 1.5rem;
          line-height: 1.7;
          font-size: 1.1rem;
        }

        .app-uses {
          list-style: none;
        }

        .app-uses li {
          padding: 0.4rem 0;
          color: #4A5260;
          position: relative;
          padding-left: 1.5rem;
          font-weight: 500;
        }

        .app-uses li::before {
          content: '✓';
          position: absolute;
          left: 0;
          color: #7B3FE4;
          font-weight: bold;
        }

        /* Reverse layout for even cards */
        .application-card:nth-child(even) .app-layout {
          grid-template-columns: 1fr 1fr;
        }

        .application-card:nth-child(even) .app-media {
          order: 2;
        }

        .application-card:nth-child(even) .app-content {
          order: 1;
        }

        /* Quality Features Section */
        .quality-section {
          padding: 100px 0;
          background: white;
        }

        .quality-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2.5rem;
        }

        .quality-card {
          background: #F7FAFC;
          padding: 3rem 2.5rem;
          border-radius: 20px;
          text-align: center;
          transition: all 0.4s ease;
          border: 2px solid transparent;
        }

        .quality-card:hover {
          border-color: #7B3FE4;
          transform: translateY(-8px);
          box-shadow: 0 20px 50px rgba(123, 63, 228, 0.15);
        }

        .quality-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #9A60FF, #7B3FE4);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          transition: all 0.3s ease;
        }

        .quality-card:hover .quality-icon {
          transform: scale(1.1);
        }

        .quality-icon i {
          font-size: 2rem;
          color: white;
        }

        .quality-card h3 {
          font-size: 1.3rem;
          font-weight: 600;
          color: #1E2329;
          margin-bottom: 1rem;
        }

        .quality-card p {
          color: #4A5260;
          line-height: 1.7;
        }

        /* CTA Section */
        .cta-section {
          padding: 100px 0;
          background: linear-gradient(135deg, #1E2329 0%, #4A5260 100%);
          text-align: center;
          color: white;
        }

        .cta-content h2 {
          font-size: 2.8rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
        }

        .cta-content p {
          font-size: 1.3rem;
          margin-bottom: 2.5rem;
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          opacity: 0.9;
        }

        .cta-buttons {
          display: flex;
          gap: 1.5rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .cta-button {
          display: inline-block;
          padding: 18px 40px;
          background: white;
          color: #7B3FE4;
          text-decoration: none;
          border-radius: 50px;
          font-weight: 700;
          font-size: 1.1rem;
          transition: all 0.4s ease;
          cursor: pointer;
          border: none;
          box-shadow: 0 10px 30px rgba(255, 255, 255, 0.2);
        }

        .cta-button:hover {
          transform: translateY(-3px) scale(1.05);
          box-shadow: 0 15px 40px rgba(255, 255, 255, 0.3);
        }

        .cta-button-secondary {
          background: transparent;
          color: white;
          border: 2px solid white;
        }

        .cta-button-secondary:hover {
          background: white;
          color: #7B3FE4;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .hero h1 {
            font-size: 2.5rem;
          }

          .hero-subtitle {
            font-size: 1.1rem;
          }

          .section-header h2 {
            font-size: 2.2rem;
          }

          .container {
            padding: 0 1rem;
          }

          .app-layout {
            grid-template-columns: 1fr;
          }

          .application-card:nth-child(even) .app-layout {
            grid-template-columns: 1fr;
          }

          .application-card:nth-child(even) .app-media,
          .application-card:nth-child(even) .app-content {
            order: unset;
          }

          .app-content {
            padding: 2rem;
          }

          .cta-buttons {
            flex-direction: column;
            align-items: center;
          }

          .showcase-image-placeholder {
            height: 300px;
          }

          .image-placeholder-content i {
            font-size: 3rem;
          }

          .image-placeholder-text {
            font-size: 1.1rem;
          }
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeInUp 0.8s ease-out;
        }
      `}</style>

      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.7.2/css/all.min.css" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-icon animate-fade-in">
            <i className="fas fa-circle"></i>
          </div>
          <h1 className="animate-fade-in">Aluminium Circles & Blanks</h1>
          <p className="hero-subtitle animate-fade-in">
            Premium quality aluminium circles and blanks manufactured to precise specifications for diverse industrial applications
          </p>
        </div>
      </section>



      {/* Specifications Section */}
      <section className="specs-section">
        <div className="container">
          <div className="section-header">
            <h2>Technical Specifications</h2>
            <p>Comprehensive specifications ensuring quality and precision in every product</p>
          </div>
          
          <div className="specs-grid">
            {specifications.map((spec, index) => (
              <div key={index} className="spec-card animate-on-scroll">
                <div className="spec-icon">
                  <i className={spec.icon}></i>
                </div>
                <h3>{spec.title}</h3>
                <ul className="spec-list">
                  {spec.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="applications-section">
        <div className="container">
          <div className="section-header">
            <h2>Industry Applications</h2>
            <p>Versatile applications across multiple industries and manufacturing processes</p>
          </div>
          
          <div className="applications-grid">
            {applications.map((app, index) => (
              <div key={index} className="application-card animate-on-scroll">
                <div className="app-layout">
                  <div className="app-media">
                    <img
                      src={index === 0 ? "/images/Circles/aluminium-bottle-cap-1000x1000.jpg" :
                           index === 1 ? "/images/Circles/aluminium-reflector-1000x1000.jpg" :
                           index === 2 ? "/images/Circles/aluminum-can-1000x1000.jpg" :
                           "/images/Circles/aluminium-bottle-cap-1000x1000.jpg"}
                      alt={app.title}
                      loading="lazy"
                      style={{width: '100%', height: '100%', minHeight: '400px', objectFit: 'cover'}}
                    />
                  </div>
                  <div className="app-content">
                    <div className="app-header">
                      <div className="app-icon">
                        <i className={app.icon}></i>
                      </div>
                      <h3>{app.title}</h3>
                    </div>
                    <p className="app-description">{app.description}</p>
                    <ul className="app-uses">
                      {app.uses.map((use, i) => (
                        <li key={i}>{use}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Features Section */}
      <section className="quality-section">
        <div className="container">
          <div className="section-header">
            <h2>Quality Assurance</h2>
            <p>Superior quality standards maintained throughout the manufacturing process</p>
          </div>
          
          <div className="quality-grid">
            {qualityFeatures.map((feature, index) => (
              <div key={index} className="quality-card animate-on-scroll">
                <div className="quality-icon">
                  <i className={feature.icon}></i>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Get Premium Quality Circles & Blanks</h2>
            <p>
              Contact us for custom specifications, bulk orders, and technical consultation for your aluminium circle requirements. Our experienced team is ready to provide solutions tailored to your specific needs.
            </p>
            <div className="cta-buttons">
              <button className="cta-button" onClick={() => window.location.href = '/contact/enquiry-form'}>
                Request Quote
              </button>
              <button className="cta-button cta-button-secondary" onClick={() => window.location.href = '/businesses'}>
                View All Products
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CirclesBlanksPage;